'use client';

import type { ReferenceCategory } from '@eliadmin/core/src/api/references/references-types';

import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchReferences } from '@eliadmin/core/src/api/references/references-api';
import {
  ReferenceCard,
  ReferenceCardSkeleton,
} from '@eliadmin/core/src/components/reference/reference-card';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Paragraph } from '@eliadmin/core/src/components/ui/paragraph';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@eliadmin/core/src/components/ui/tabs';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { LuArrowDown } from 'react-icons/lu';

export function ReferencesList({
  locale,
  categories,
}: {
  locale: string;
  categories: ReferenceCategory[];
}) {
  const { t } = useTranslations();

  const [activeTab, setActiveTab] = useState(categories[0] ? String(categories[0].id) : '');
  const activeTabId = activeTab ? Number(activeTab) : undefined;

  const referencesQuery = useInfiniteQuery({
    queryKey: [CacheKey.REFERENCES, locale, activeTabId],
    queryFn: async ({ pageParam = 1 }) => {
      return await fetchReferences({
        locale,
        query: {
          page: pageParam,
          results: 9,
          order: 'desc',
          orderBy: 'created_at',
          where: activeTabId ? { categories: { equals: activeTabId } } : undefined,
        },
      });
    },
    getNextPageParam: (lastPage: any) => {
      if (lastPage?.meta?.current_page < lastPage?.meta?.last_page)
        return lastPage?.meta?.current_page + 1;
      return undefined;
    },
    initialPageParam: 1,
  });

  const references = referencesQuery.data?.pages.flatMap((page: any) => page.data) ?? [];
  const isLoading = referencesQuery?.isLoading;

  return (
    <Tabs
      value={activeTab}
      defaultValue={activeTab}
      onValueChange={value => setActiveTab(value)}
      className={cn('custom-container', 'flex flex-col items-center justify-center gap-12')}
    >
      <TabsList
        className={cn(
          'hide-scroll-bar',
          'flex h-fit w-full flex-nowrap items-center justify-start gap-2 overflow-scroll rounded-none bg-transparent py-0 md:justify-center lg:gap-2',
        )}
      >
        {categories.map(category => (
          <TabsTrigger
            value={String(category.id)}
            key={category.id}
            className={cn(
              'flex h-10 w-fit items-center justify-center whitespace-nowrap rounded-[4px] border border-border px-3 text-center text-sm font-normal sm:px-5 lg:px-6 lg:text-base',
              'ea-references__tabs-trigger',
            )}
          >
            {category.name}
          </TabsTrigger>
        ))}
      </TabsList>
      <TabsContent
        value={activeTab}
        className="grid w-full grid-cols-1 gap-2 pr-5 sm:gap-3 sm:pr-0 md:grid-cols-2 lg:grid-cols-3"
      >
        {referencesQuery.error && (
          <Paragraph className="col-span-1 pt-8 text-center md:col-span-2">
            {t('text2364')}
          </Paragraph>
        )}
        {isLoading ? (
          [...Array(6)].map((_, index) => <ReferenceCardSkeleton key={index} />)
        ) : references.length > 0 ? (
          <>
            {references.map(reference => (
              <ReferenceCard key={reference.id} reference={reference} />
            ))}
          </>
        ) : (
          <Paragraph
            color="muted-foreground"
            size="lg"
            className="col-span-1 text-center md:col-span-2"
          >
            {t('text2376')}
          </Paragraph>
        )}
      </TabsContent>
      {referencesQuery.hasNextPage && (
        <div className="flex w-full items-center justify-center pr-4 md:pr-0">
          <Button
            onClick={() => referencesQuery.fetchNextPage()}
            variant={'outline'}
            className="w-full sm:w-fit"
          >
            {t('text338')} <LuArrowDown size={18} />
          </Button>
        </div>
      )}
    </Tabs>
  );
}
